import {GeospatialViewerService} from '../../../../../pages/geospatial-viewer/api/geospatial-viewer.service';
import {StudiesStore} from '@store/common/studies.store';
import {Injectable} from '@angular/core';
import {NbThemeService} from '@nebular/theme';
import {WorkflowType} from '@core/interfaces/engin/workflow';
import {LayersService} from '../../../../../pages/geospatial-viewer/layers/layers.service';
import {map} from 'rxjs/operators';
import {
    FieldDataType,
    GeospatialIconType,
    GeospatialTooltipRequest,
    GeospatialTooltipResponse,
    GeospatialTooltipSection,
    GeospatialTooltipField,
    GeospatialTooltipType,
    CoordinateLatLong,
    GeospatialMetricTooltipRequest,
    GeospatialOutageTooltipRequest,
} from '../../../../../pages/geospatial-viewer/model/tooltip.model';
import Feature = __esri.Feature;
import {MapColor, MapColoursService} from '@core/utils/engin/geospatial-viewer/renderers/map-colours.service';
import {FormatsService} from '@core/utils';
import {VisualizationType} from '../../../../../pages/geospatial-viewer/model/visualization';
import {GeospatialColourType} from '../../../../../pages/geospatial-viewer/model/spatial-colours';
import {SpatialEntityDataExtended} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {Observable} from 'rxjs';

const mainLabelStyle =
    'font-family: Roboto; font-size: 14px; font-style: normal; font-weight: 700; line-height: 20px; margin-bottom: 10px;';
const subLabelStyle =
    'font-size: 14px; font-style: normal; font-weight: 400; line-height: 16px; margin-bottom: 4px; width: 50%; flex:1;';
const valueStyle =
    'font-size: 14px; font-style: normal; font-weight: 400; line-height: 16px; margin-bottom: 4px; width: 50%; flex:1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;';
const divStyle = 'display: flex;';

@Injectable()
export class GenericPopupService {
    constructor(
        private studiesStore: StudiesStore,
        private geospatialViewerService: GeospatialViewerService,
        private nbThemeService: NbThemeService,
        private layerService: LayersService,
    ) {}

    /**
     * General tooltip handling for METRIC layer.
     * Note: when implementing from a general service,
     *  `content: this.genericPopupService.getGenericPopupFunction.bind(this.genericPopupService, metrics || []),`
     * - extra args will be passed here as arg1 onwards
     * - feature will be automatically injected as the last arg
     * - due to this, extra args MUST always be passed with values
     * @param metrics
     * @param visualizationType the rendered visualization type (response type, not request type)
     * @param feature
     */
    getGenericMetricTooltipFunction(
        metrics: any[],
        visualizationType: VisualizationType,
        feature: Feature,
    ): Promise<any> {
        return this.getMetricTooltipResponse(visualizationType, feature.graphic.attributes, metrics)
            .pipe(map((data: GeospatialTooltipResponse) => this.getGenericPopupTemplate(feature, data)))
            .toPromise();
    }

    /**
     * General tooltip handling for OUTAGE layer.
     * Similar to `getGenericMetricTooltipFunction`, but with a different request object. Split for simplicity.
     * See docstring from `getGenericMetricTooltipFunction`
     * @param metrics
     * @param visualizationType the rendered visualization type (response type, not request type)
     * @param feature
     */
    getGenericOutageTooltipFunction(
        metrics: any[],
        visualizationType: VisualizationType,
        feature: Feature,
    ): Promise<any> {
        return this.getOutageTooltipResponse(visualizationType, feature.graphic.attributes, metrics)
            .pipe(map((data: GeospatialTooltipResponse) => this.getGenericPopupTemplate(feature, data)))
            .toPromise();
    }

    public getOutageTooltipResponse(
        visualizationType: VisualizationType,
        entityData: SpatialEntityDataExtended,
        metrics?: any[],
    ): Observable<GeospatialTooltipResponse> {
        const entityId: string = entityData.entityId;
        const entityType: string = entityData.entityType;
        const tooltipType: GeospatialTooltipType = entityData.tooltipType;

        if (!GeospatialTooltipType.isTypeOutage(tooltipType)) {
            throw new Error('Tooltip type not supported in Outage layer handler: ' + tooltipType);
        }

        const tooltipRequest: GeospatialOutageTooltipRequest = {
            tooltipType,
            entityId,
            entityType,
            visualizationType,
            outageMetrics: this.layerService.outageLayers
                .filter((l) => l.getOutageMetric() && l.getOutageViewBy())
                .map((layer) => layer.getOutageMetric()),
            // filters from the first outage layer
            filters: this.layerService.outageLayers
                .find((l) => l.getOutageMetric() && l.getOutageViewBy())
                ?.getFilters(),
        };

        return this.geospatialViewerService.getGeospatialTooltipData(null, tooltipRequest);
    }

    /**
     * Method for requesting tooltip data for Metric and Project layer tooltips
     * @param visualizationType the rendered visualization type (response type, not request type)
     * @param entityData entity data for tooltip
     * @param metrics May be null. Metrics displayed with the tooltip
     */
    public getMetricTooltipResponse(
        visualizationType: VisualizationType,
        entityData: SpatialEntityDataExtended,
        metrics?: any[],
    ): Observable<GeospatialTooltipResponse> {
        const entityId: string = entityData.entityId;
        const entityType: string = entityData.entityType;
        const tooltipType: GeospatialTooltipType = entityData.tooltipType;

        // This method supports: Metric layer, Project layer, Substations
        if (
            !(
                GeospatialTooltipType.isTypeMetric(tooltipType) ||
                GeospatialTooltipType.isTypeProject(tooltipType) ||
                GeospatialTooltipType.isTypeSubstation(tooltipType)
            )
        ) {
            throw new Error('Tooltip type not supported in Metric layer handler: ' + tooltipType);
        }

        // Some tooltips do not need studyId
        let studyId: number = this.studiesStore.getActiveStudyIdByType(WorkflowType.STUDY);

        // Some tooltips need extra info from active layers
        let reqMetrics: any[] =
            metrics && metrics.length > 0
                ? metrics
                : this.layerService.metricLayers
                      .filter((l) => l.getMeasure() && l.getMetric() && l.getVisualizationType())
                      .map((layer) => layer.getMetric());

        const tooltipRequest = {
            tooltipType,
            entityId,
            entityType,
            visualizationType,
            metrics: reqMetrics,
        } as GeospatialTooltipRequest;
        return this.geospatialViewerService.getGeospatialTooltipData(studyId, tooltipRequest);
    }

    private getGenericPopupTemplate(feature: Feature, data: GeospatialTooltipResponse) {
        const template = document.createElement('div');

        template.innerHTML = `
            <div style="margin-top: 7px;">
                <div style="${divStyle}">
                    ${this.getIcon(data.title.iconType)}
                    <p style="${mainLabelStyle}">${data.title.label}</p>
                </div>
                ${this.getSectionsHtml(data.sections)}
                ${this.getExternalLinks(data.centerCoordinate)}
            </div>`;

        return template;
    }

    /** Get icon, for tooltip header **/
    private getIcon(entityIcon: GeospatialIconType): string {
        let icon = '';
        switch (entityIcon) {
            case GeospatialIconType.POINT:
                icon = '<calcite-icon icon="bullet-point" style="margin-right: 8px"></calcite-icon>';
                break;
            case GeospatialIconType.LINE:
                icon = '<calcite-icon icon="line-straight" scale="s" style="margin-right: 8px"></calcite-icon>';
                break;
            case GeospatialIconType.POLYGON:
                icon = '<calcite-icon icon="polygon-area" style="margin-right: 8px"></calcite-icon>';
                break;
        }
        return icon;
    }

    /** Get HTML for each tooltip section **/
    private getSectionsHtml(list: GeospatialTooltipSection[]): string {
        return list
            .sort((a, b) => a.order - b.order)
            .map((section) => {
                const sectionHeader = section.label || '';
                const fieldsHtml = section.fields.map((field) => this.renderField(field)).join('');
                return `<div style="margin-top: 16px"><p style="${mainLabelStyle}">${sectionHeader}</p>${fieldsHtml}</div>`;
            })
            .join('');
    }

    /** Get HTML for each tooltip section > field **/
    private renderField(field: GeospatialTooltipField): string {
        // Field rendering may fail, and causes the entire tooltip to not render
        try {
            const fieldLabel: string = field.label;
            const fieldValue: string = this.formatFieldValue(field) || '—';

            const displayColour: string = this.getMapCategoryColour(field);

            const colorIndicator = displayColour
                ? `<svg height="8" width="8" style="vertical-align:baseline;"><circle cx="4" cy="4" r="4" fill="${displayColour}"/></svg>`
                : '';

            return `<div style="${divStyle}">
                    <p style="${subLabelStyle}">${fieldLabel}</p>
                    <p style="${valueStyle}">${fieldValue} ${colorIndicator}</p>
                </div>`;
        } catch {
            return '';
        }
    }

    /** Prepare value for a single field */
    private formatFieldValue(field: GeospatialTooltipField): string {
        if (field.value == null) {
            return 'N/A';
        }

        switch (field.dataType) {
            // String: no value preparation
            case FieldDataType.STRING:
                return field.value;
            // Numeric: prepare numeric value
            case FieldDataType.DOUBLE:
            case FieldDataType.LONG:
                return FormatsService.prepareValueFromUnit(field.value, field.unit);
            // Other types: no value preparation
            default:
                return field.value;
        }
    }

    /** Get HTML for external links section. This is optional, so may return empty string. **/
    private getExternalLinks(coordinate: CoordinateLatLong): string {
        if (coordinate == null) return '';

        const lat: number = coordinate.latitude;
        const long: number = coordinate.longitude;

        const streetViewUrl = `http://maps.google.com/maps?layer=c&cbll=${lat},${long}`;
        const mapsUrl = `http://maps.google.com/maps?ll=${lat},${long}`;
        return lat && long
            ? `
        <div class='row' style="margin-top: 16px">
            <div class='col-3'>
            <p style="${mainLabelStyle}">External links: </p>
            </div>
            <a class='col-4' href="${streetViewUrl}" target="_blank" style="display: flex;">
              Google Street View
              <calcite-icon style="margin-left: 4px; width: 16px; height: 16px; min-height: 0; min-width: 0;" icon="launch"/>
            </a>
            <a class='col-4' href="${mapsUrl}" target="_blank" style="display: flex;">
              Google Maps
              <calcite-icon style="margin-left: 4px; width: 16px; height: 16px; min-height: 0; min-width: 0;" icon="launch"/>
            </a>
        </div>
        `
            : '';
    }

    /** Get colour, or default to null when colour is not provided */
    private getMapCategoryColour(field: GeospatialTooltipField): string {
        if (field.colourType == null) return null;
        if (field.colourType === GeospatialColourType.NONE) return null;

        // Find specific colour
        const r: MapColor = MapColoursService.list(this.nbThemeService.currentTheme).find(
            (mapColor: MapColor) => mapColor.value === field.colourType,
        );
        if (r != null) return r.color;

        // Else fallback to "no colour" colour
        return MapColoursService.getNoColour(this.nbThemeService.currentTheme).color;
    }
}
